import { useDispatch, useSelector } from "react-redux";
import {
	authStateResolved,
	loginStart,
	loginSuccess,
	loginFailure,
	logoutSuccess,
	clearAuthError,
	selectUser,
	selectIsAuthenticated,
	selectAuthLoading,
	selectAuthError,
	selectAuthInitializing,
} from "../redux/features/authSlice";
import {
	loginWithEmailPassword,
	logoutUser,
	subscribeToAuthChanges,
} from "../services/firebase/authService";

/**
 * Central auth hook.
 * Use this hook in any component that needs auth state or actions.
 */
const useAuth = () => {
	const dispatch = useDispatch();

	const user = useSelector(selectUser);
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const loading = useSelector(selectAuthLoading);
	const error = useSelector(selectAuthError);
	const initializing = useSelector(selectAuthInitializing);

	/**
	 * Bootstrap Firebase auth listener once at the app level (AuthProvider).
	 * Do NOT call this from individual components.
	 */
	const initAuthListener = () => {
		const unsubscribe = subscribeToAuthChanges((user) => {
			dispatch(authStateResolved(user));
		});
		return unsubscribe;
	};

	/** Log in with email/password. */
	const login = async (email, password) => {
		dispatch(loginStart());
		try {
			const serializedUser = await loginWithEmailPassword(
				email,
				password,
			);
			dispatch(loginSuccess(serializedUser));
			return { success: true };
		} catch (err) {
			const message = mapFirebaseError(err.code);
			dispatch(loginFailure(message));
			return { success: false, message };
		}
	};

	/** Log out. Call from your navbar logout button. */
	const logout = async () => {
		try {
			await logoutUser();
			dispatch(logoutSuccess());
		} catch {
			// Force local logout even if Firebase call fails
			dispatch(logoutSuccess());
		}
	};

	const clearError = () => dispatch(clearAuthError());

	return {
		user,
		isAuthenticated,
		loading,
		error,
		initializing,
		login,
		logout,
		clearError,
		initAuthListener,
	};
};

// ─── Map Firebase error codes to friendly messages ────────────────────────────

const mapFirebaseError = (code) => {
	switch (code) {
		case "auth/user-not-found":
		case "auth/wrong-password":
		case "auth/invalid-credential":
			// Intentionally vague — don't reveal which field is wrong (security)
			return "Invalid email or password.";
		case "auth/invalid-email":
			return "Please enter a valid email address.";
		case "auth/user-disabled":
			return "This account has been disabled. Contact support.";
		case "auth/too-many-requests":
			return "Too many failed attempts. Account temporarily locked. Try again later or reset your password.";
		case "auth/network-request-failed":
			return "Network error. Please check your connection.";
		default:
			return "Something went wrong. Please try again.";
	}
};

export default useAuth;
